import { useState, useEffect } from 'react';

const FETCH_TIMEOUT_MS = 10000;

type ResourceStatus = 'idle' | 'loading' | 'success' | 'error' | 'timeout';

export function useTranscription(
  transcriptionUrl: string | null | undefined,
  inlineTranscription?: any,
) {
  const [transcription, setTranscription] = useState<any>(inlineTranscription || null);
  const [loading, setLoading] = useState<boolean>(!!transcriptionUrl && !inlineTranscription);
  const [status, setStatus] = useState<ResourceStatus>(inlineTranscription ? 'success' : transcriptionUrl ? 'loading' : 'idle');
  const [error, setError] = useState<Error | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!transcriptionUrl) {
      setTranscription(inlineTranscription || null);
      setLoading(false);
      setStatus(inlineTranscription ? 'success' : 'idle');
      setError(null);
      setTimedOut(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    let cancelled = false;
    const startedAt = performance.now();

    setLoading(true);
    setStatus('loading');
    setError(null);
    setTimedOut(false);

    fetch(transcriptionUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Transcription request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setTranscription(data);
          setLoading(false);
          setStatus('success');
          console.info('[clips] transcription loaded', {
            url: transcriptionUrl,
            durationMs: Math.round(performance.now() - startedAt),
          });
        }
      })
      .catch((err) => {
        console.error('Error fetching transcription:', err);
        if (!cancelled) {
          const didTimeOut = err?.name === 'AbortError' || err === 'timeout' || err?.message === 'timeout';
          setTranscription(inlineTranscription || null);
          setLoading(false);
          setStatus(didTimeOut ? 'timeout' : 'error');
          setError(err instanceof Error ? err : new Error(String(err)));
          setTimedOut(didTimeOut);
          console.warn('[clips] transcription fetch failed', {
            url: transcriptionUrl,
            durationMs: Math.round(performance.now() - startedAt),
            timedOut: didTimeOut,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [transcriptionUrl, inlineTranscription]);

  return { data: transcription, loading, status, error, timedOut };
}
